import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { TextInput } from '../../../../components/TextInput';
import {
  getAllMatchingProspects,
  getDefaultTagDefinitions,
  getGradYears,
  getProspects,
} from '../../../../services/prospects';
import { getTeams } from '../../../../services/teams';
import { attachProspectsToScholarship } from '../../../../services/scholarships';
import type { ProspectResponse, TeamResponse } from '../../../../types/api';
import type { ScholarshipPlayer } from '../../../../types/scholarships';
import type { DefaultTagDefinition, TagFilters } from '../../../../types/tags';
import { countActiveFilters, GRAD_YEAR_FILTER_KEY } from '../../../../types/tags';
import { FilterChip } from '../players/FilterChip';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import './AttachProspectsModal.css';
import '../players/FilterChip.css';

interface AttachProspectsModalProps {
  scholarshipId: string;
  /** Prospect ids already on this roster, so they can be shown as attached rather than offered twice. */
  attachedProspectIds: string[];
  onClose: () => void;
  onAttached: (players: ScholarshipPlayer[]) => void;
}

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Picks players out of the prospect pool and puts them on a roster.
 *
 * Reads the pool through the ordinary `getProspects` call rather than anything roster-specific.
 * That endpoint already does search, repeatable grad year, tag filters and paging, and
 * `appendTagFilterParams` already encodes the `tag_*` convention the API shares with the
 * messaging recipient picker. A second query path here is exactly the divergence TagFilterQuery's
 * doc comment warns about: filter for a tag, see one set of players on the board and a different
 * set in the pool.
 *
 * Grad year is a filter chip, not a property of the roster. A board fills up with mostly one
 * class and a couple of others, so making it board identity would have been a lie.
 */
export const AttachProspectsModal = ({
  scholarshipId,
  attachedProspectIds,
  onClose,
  onAttached,
}: AttachProspectsModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const [definitions, setDefinitions] = useState<DefaultTagDefinition[]>([]);
  const [gradYearOptions, setGradYearOptions] = useState<number[]>([]);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [teamQuery, setTeamQuery] = useState('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [isSelectingTeam, setIsSelectingTeam] = useState(false);
  const [filters, setFilters] = useState<TagFilters>({});
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [prospects, setProspects] = useState<ProspectResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * The ids the last "Select all" picked up, so its checkbox can show whether they are all still
   * selected and unticking it can take back exactly those. Null until the coach uses it, and
   * dropped whenever the question changes, since the set no longer describes what matches.
   */
  const [matchedIds, setMatchedIds] = useState<string[] | null>(null);
  /** The select-all read came back capped, so matchedIds is only the first slice of the match. */
  const [matchTruncated, setMatchTruncated] = useState(false);
  /**
   * Bumped whenever the question changes. A select-all read that comes back after the coach has
   * picked another team or filter answers the old question, and must not add those players or
   * tick the box beside the new one.
   */
  const queryVersion = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const alreadyAttached = useMemo(
    () => new Set(attachedProspectIds),
    [attachedProspectIds],
  );

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isSaving, onClose]);

  useEffect(() => {
    const trigger = (document.activeElement as HTMLElement) ?? null;
    return () => {
      trigger?.focus?.();
    };
  }, []);

  // Filter options. A failure here is reported rather than swallowed: silently showing an
  // empty chip list would read as "this org has no tags", which is a different fact.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [defs, years, teamList] = await Promise.all([
          getDefaultTagDefinitions(),
          getGradYears(),
          getTeams(),
        ]);
        if (cancelled) return;
        setDefinitions(defs);
        setGradYearOptions(years);
        setTeams(teamList);
      } catch (e) {
        if (!cancelled) {
          setLoadError(getSafeErrorMessage(e, 'Failed to load filter options'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any change to what is being asked for restarts at page 1 — otherwise a narrowed filter
  // can land on a page that no longer exists and show an empty list over a non-empty result.
  useEffect(() => {
    setPage(1);
    setMatchedIds(null);
    setMatchTruncated(false);
    queryVersion.current += 1;
  }, [searchTerm, filters, teamId]);

  const loadProspects = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getProspects({
        pageNumber: page,
        pageSize: PAGE_SIZE,
        teamId: teamId ?? undefined,
        searchTerm: searchTerm || undefined,
        tagFilters: filters,
      });
      setProspects(result.items);
      setTotalCount(result.totalCount);
    } catch (e) {
      setLoadError(getSafeErrorMessage(e, 'Failed to load players'));
      setProspects([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchTerm, filters, teamId]);

  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  const handleToggleFilter = (tagName: string, value: string) => {
    setFilters((prev) => {
      const current = prev[tagName] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      const updated = { ...prev };
      if (next.length === 0) {
        delete updated[tagName];
      } else {
        updated[tagName] = next;
      }
      return updated;
    });
  };

  const toggleSelected = (prospectId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(prospectId)) {
        next.delete(prospectId);
      } else {
        next.add(prospectId);
      }
      return next;
    });
  };

  // Select-all covers the page in front of the coach, not the whole filtered result set.
  // "Select all" over 400 unseen players is not a choice anyone can verify before pressing
  // Attach, and the count on the button would not match what they had looked at.
  const selectablePageIds = prospects
    .map((p) => p.id)
    .filter((id) => !alreadyAttached.has(id));
  const allPageSelected =
    selectablePageIds.length > 0 && selectablePageIds.every((id) => selected.has(id));
  const somePageSelected =
    !allPageSelected && selectablePageIds.some((id) => selected.has(id));

  const handleSelectPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        selectablePageIds.forEach((id) => next.delete(id));
      } else {
        selectablePageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectedTeam = teams.find((t) => t.id === teamId) ?? null;

  // True between a keystroke and the debounced term catching up, i.e. while totalCount still
  // describes the previous query.
  const isSearchPending = searchInput.trim() !== searchTerm;

  // Teams filtered client-side. getTeams returns the org's whole list in one call, so a
  // debounced server search would add a round trip per keystroke to filter a list already in
  // memory. Substring rather than prefix — coaches type "elite", not "ohio".
  //
  // An empty query shows NOTHING rather than every team: the list sits above the grad-year and
  // tag chips, and an org with forty teams would push the filters a coach reaches for most
  // often below the fold before they had even asked about teams.
  const trimmedTeamQuery = teamQuery.trim();
  const matchingTeams = trimmedTeamQuery.length === 0
    ? []
    : teams.filter((t) => t.name.toLowerCase().includes(trimmedTeamQuery.toLowerCase()));

  /**
   * Selects every player matching the current filters, across all pages.
   *
   * This started out page-scoped, on the reasoning that a coach cannot verify 400 unseen
   * players before pressing Attach. That reasoning was wrong about the cost: filtering to a
   * team and then paging through it ticking "select this page" is the actual job, and it is
   * tedious enough that the safety it bought was not worth it. The count on the button, the
   * count in the footer, and the fact that a wrongly attached row can just be removed are the
   * honest safeguards.
   *
   * Reads through getAllMatchingProspects, which reports isTruncated separately from items, so
   * a capped result is reported rather than passed off as the whole set.
   */
  const handleSelectAllMatching = async () => {
    setIsSelectingTeam(true);
    setLoadError(null);
    const version = queryVersion.current;
    try {
      const all = await getAllMatchingProspects({
        teamId: teamId ?? undefined,
        // The LIVE input, not the debounced term. A coach who types a name and clicks
        // straight through would otherwise select the team unfiltered, because the debounce
        // had not landed yet — silently attaching players they had just filtered out.
        searchTerm: searchInput.trim() || undefined,
        tagFilters: filters,
      });
      if (version !== queryVersion.current) return;

      const selectable = all.items
        .map((p) => p.id)
        .filter((id) => !alreadyAttached.has(id));
      setSelected((prev) => new Set([...prev, ...selectable]));
      setMatchedIds(selectable);
      setMatchTruncated(all.isTruncated);

      if (all.isTruncated) {
        setLoadError(
          `${all.totalCount} players match, more than the ${all.maxRecords} we can select at ` +
          `once. The first ${all.items.length} are selected — narrow the filters and repeat ` +
          `for the rest.`,
        );
      }
    } catch (e) {
      if (version !== queryVersion.current) return;
      setLoadError(getSafeErrorMessage(e, 'Failed to select all matching players'));
    } finally {
      setIsSelectingTeam(false);
    }
  };

  const capturedAllSelected =
    matchedIds != null && matchedIds.length > 0 && matchedIds.every((id) => selected.has(id));
  // A capped read selected only part of what matches, so the box says "some", not "all".
  const allMatchingSelected = capturedAllSelected && !matchTruncated;
  const someMatchingSelected =
    !allMatchingSelected && matchedIds != null && matchedIds.some((id) => selected.has(id));

  const handleToggleAllMatching = () => {
    if (capturedAllSelected && matchedIds) {
      setSelected((prev) => {
        const next = new Set(prev);
        matchedIds.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    void handleSelectAllMatching();
  };

  const handleAttach = async () => {
    if (selected.size === 0) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const players = await attachProspectsToScholarship(scholarshipId, [...selected]);
      onAttached(players);
    } catch (e) {
      // The server is all-or-nothing, so a failure here means the board is unchanged —
      // the selection stays put so the coach can retry rather than rebuild it.
      setSaveError(getSafeErrorMessage(e, 'Failed to attach players'));
    } finally {
      setIsSaving(false);
    }
  };

  const activeFilterCount = countActiveFilters(filters);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="attach-prospects-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attach-prospects-title"
    >
      <div className="attach-prospects">
        <header className="attach-prospects__header">
          <div>
            <h2 id="attach-prospects-title" className="attach-prospects__title">
              Attach players
            </h2>
            <p className="attach-prospects__subtitle">
              Filter your players, then attach them to this roster.
            </p>
          </div>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Close
          </Button>
        </header>

        <div className="attach-prospects__body">
          <aside className="attach-prospects__filters">
            <section className="attach-prospects__filter-group">
              <span className="attach-prospects__filter-label" id="attach-team-label">
                Team
              </span>
              {selectedTeam ? (
                <div className="attach-prospects__team-selected">
                  <span className="attach-prospects__team-name">{selectedTeam.name}</span>
                  <button
                    type="button"
                    className="attach-prospects__reset"
                    onClick={() => {
                      setTeamId(null);
                      setTeamQuery('');
                    }}
                  >
                    Clear team
                  </button>
                </div>
              ) : (
                <>
                  <TextInput
                    label="Find a team"
                    value={teamQuery}
                    onChange={(e) => setTeamQuery(e.target.value)}
                    placeholder="Search teams"
                  />
                  {/*
                    role="listbox" over the matches rather than a <select>: the list is
                    filtered as you type and each row is a real button, so keyboard users tab
                    through the same options they can see.
                  */}
                  {trimmedTeamQuery.length === 0 ? (
                    <p className="attach-prospects__team-hint">
                      Start typing to find a team.
                    </p>
                  ) : (
                    <ul
                      className="attach-prospects__team-list"
                      role="listbox"
                      aria-labelledby="attach-team-label"
                    >
                      {matchingTeams.length === 0 ? (
                        <li className="attach-prospects__team-empty">No teams match</li>
                      ) : (
                        matchingTeams.map((team) => (
                          <li key={team.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={false}
                              className="attach-prospects__team-option"
                              onClick={() => setTeamId(team.id)}
                            >
                              {team.name}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </>
              )}
            </section>

            <TextInput
              label="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name"
            />

            {/*
              Deliberately NOT offering the Level / JC Year facets here.

              This picker shares the `TagFilters` model and the same endpoints, so adding
              them is mechanically trivial — but scholarship attachment is a roster-money
              flow with its own reviewers, and a filter a coach can set here narrows who
              gets attached to a scholarship. Shipping the facets on the prospect list
              first keeps that blast radius separate. Nothing here is dead or wrong in the
              meantime: the reserved keys are simply never written, and the shared
              `appendTagFilterParams` would carry them correctly the day they are added
              (options live in `constants/prospectFilterFacets.ts`).
            */}
            {gradYearOptions.length > 0 && (
              <section className="attach-prospects__filter-group">
                {/*
                  span + role="group" rather than a <label> around the chips: a label naming a
                  set of controls is not a label, and trips label-has-associated-control.
                */}
                <span className="attach-prospects__filter-label" id="attach-grad-year-label">
                  Grad year
                </span>
                <div
                  className="attach-prospects__chips"
                  role="group"
                  aria-labelledby="attach-grad-year-label"
                >
                  {gradYearOptions.map((year) => (
                    <FilterChip
                      key={year}
                      value={String(year)}
                      isSelected={(filters[GRAD_YEAR_FILTER_KEY] ?? []).includes(String(year))}
                      onClick={() => handleToggleFilter(GRAD_YEAR_FILTER_KEY, String(year))}
                      isSingleSelect={false}
                    />
                  ))}
                </div>
              </section>
            )}

            {definitions.map((def) => (
              <section key={def.tagName} className="attach-prospects__filter-group">
                <span
                  className="attach-prospects__filter-label"
                  id={`attach-tag-${def.tagName.replace(/\s+/g, '-')}`}
                >
                  {def.tagName}
                </span>
                <div
                  className="attach-prospects__chips"
                  role="group"
                  aria-labelledby={`attach-tag-${def.tagName.replace(/\s+/g, '-')}`}
                >
                  {def.options.map((option) => (
                    <FilterChip
                      key={option}
                      value={option}
                      isSelected={(filters[def.tagName] ?? []).includes(option)}
                      onClick={() => handleToggleFilter(def.tagName, option)}
                      isSingleSelect={false}
                    />
                  ))}
                </div>
              </section>
            ))}

            {activeFilterCount > 0 && (
              <button
                type="button"
                className="attach-prospects__reset"
                onClick={() => setFilters({})}
              >
                Clear {activeFilterCount} tag filter{activeFilterCount === 1 ? '' : 's'}
              </button>
            )}
          </aside>

          <div className="attach-prospects__results">
            <div className="attach-prospects__results-header">
              <span>
                <strong>{totalCount}</strong> {totalCount === 1 ? 'player' : 'players'} match
              </span>
              {/*
                Only offered when the result actually spans more than one page — on a single
                page it would do the same thing as the control beside it, and two buttons that
                do the same thing is how a coach ends up unsure which one they pressed.
              */}
              {/*
                Checkboxes rather than text links: coaches did not see "Select all 28 in ..." as
                something to click until it had a box beside it (coach feedback, 10/2).
              */}
              {totalCount > PAGE_SIZE && (
                <Checkbox
                  className="attach-prospects__select-all"
                  checked={allMatchingSelected}
                  indeterminate={someMatchingSelected}
                  onChange={handleToggleAllMatching}
                  // Disabled while a typed search has not yet been applied: the count comes
                  // from the last completed query, so acting on it mid-debounce would put a
                  // number on the label that does not match what the read returns.
                  disabled={isSelectingTeam || isSearchPending}
                  label={
                    isSelectingTeam
                      ? 'Selecting...'
                      : isSearchPending
                        ? selectedTeam
                          ? `Select all in ${selectedTeam.name}`
                          : 'Select all matching'
                        : selectedTeam
                          ? `Select all ${totalCount} in ${selectedTeam.name}`
                          : `Select all ${totalCount} matching`
                  }
                />
              )}
              {selectablePageIds.length > 0 && (
                <Checkbox
                  className="attach-prospects__select-page"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={handleSelectPage}
                  label="Select this page"
                />
              )}
            </div>

            {loadError && (
              <div className="attach-prospects__error" role="alert">
                {loadError}
              </div>
            )}

            {isLoading ? (
              <p className="attach-prospects__empty">Loading players...</p>
            ) : prospects.length === 0 ? (
              <p className="attach-prospects__empty">
                No players match these filters.
              </p>
            ) : (
              <ul className="attach-prospects__list">
                {prospects.map((prospect) => {
                  const isAttached = alreadyAttached.has(prospect.id);
                  return (
                    <li key={prospect.id} className="attach-prospects__row">
                      <Checkbox
                        className="attach-prospects__row-toggle"
                        checked={isAttached || selected.has(prospect.id)}
                        // Already on the roster: shown ticked and locked rather than hidden,
                        // so a coach searching for someone they added last week finds them
                        // instead of concluding the player is missing from the pool.
                        disabled={isAttached}
                        onChange={() => toggleSelected(prospect.id)}
                        label={
                          <span className="attach-prospects__row-main">
                            <span className="attach-prospects__row-name">
                              {prospect.firstName} {prospect.lastName}
                            </span>
                            <span className="attach-prospects__row-meta">
                              {[
                                prospect.position,
                                prospect.gradYear ? String(prospect.gradYear) : null,
                                // First team rather than a school field — ProspectResponse
                                // carries teams, and a player's club is what a coach
                                // recognises them by in a long list.
                                prospect.teams?.[0]?.name ?? null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                              {isAttached && ' · already on this roster'}
                            </span>
                          </span>
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {totalPages > 1 && (
              <div className="attach-prospects__pager">
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                >
                  Previous
                </Button>
                <span className="attach-prospects__pager-label">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>

        <footer className="attach-prospects__footer">
          {saveError && (
            <div className="attach-prospects__error" role="alert">
              {saveError}
            </div>
          )}
          <span className="attach-prospects__selected-count">
            {selected.size} selected
          </span>
          {/*
            The counterpart to select-all: a selection of two hundred needs one way out that
            is not closing the modal and starting over.
          */}
          {selected.size > 0 && (
            <button
              type="button"
              className="attach-prospects__clear-selection"
              onClick={() => setSelected(new Set())}
              disabled={isSaving}
            >
              Clear selection
            </button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAttach}
            disabled={selected.size === 0 || isSaving}
          >
            {isSaving
              ? 'Attaching...'
              : `Attach ${selected.size || ''}`.trim()}
          </Button>
        </footer>
      </div>
    </div>
  );
};
